import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from "react-hook-form";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Grid,
    CircularProgress,
    Card,
    CardContent,
    useTheme,
    Switch,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    GlobalStyles
} from '@mui/material';
import { toast } from 'react-toastify';
import { getGoogleAdmob, updateGoogleAdmob } from '../../Redux/Reducers/SettingsReducer';
import usePermissions from '../../hooks/usePermissions';


const MODULE_KEY = 'settings';

const GoogleAdMobSettings = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { hasAction } = usePermissions();
    const { admobSettings, loading } = useSelector((state) => state.settings);
    const [targetOptions, setTargetOptions] = useState([]);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            is_enabled: true,
            show_ads_to_free_users: true,
        },
    });

    useEffect(() => {
        dispatch(getGoogleAdmob()).catch((err) => {
            toast.error(err);
        });
    }, [dispatch]);

    useEffect(() => {
        if (!admobSettings) return;

        // Convert target_options to dropdown
        const options = Object.entries(admobSettings.target_options || {}).map(
            ([value, label]) => ({
            value,
            label,
            })
        );

        setTargetOptions(options);

        // Set form values
        reset({
            admob_enabled: admobSettings.admob_enabled,
            admob_user_targeting: admobSettings.admob_user_targeting,
        });
    }, [admobSettings, reset]);

    const onSubmit = async (data) => {
        try {
            await dispatch(updateGoogleAdmob(data)).unwrap();
            toast.success(t('settings_updated', 'Settings updated successfully!'));
        } catch (err) {
            toast.error(err);
        }
    };

    return (
        <Box sx={{ p: '36px 24px 24px', maxWidth: '900px' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px', mb: '24px', }} className='main_title'>{t('google_admob_settings', 'Google AdMob Settings')}</Typography>
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px' }}>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container>
                            <Grid item xs={12} sx={{ mb: 2 }}>
                                <Controller
                                    name="admob_enabled"
                                    control={control}
                                    defaultValue={false}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            sx={{ 
                                                ml: '-5px',
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '14px',
                                                    color: '#fff',
                                                },
                                            }}
                                            control={
                                                <Switch
                                                    {...field}
                                                    checked={!!field.value}
                                                    color="success"
                                                    
                                                />
                                            }
                                            label={t('settings.google_admob.admob_enabled',"AdMob Enabled")}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ mb: 3 }}>
                                <Controller
                                    name="admob_user_targeting"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl fullWidth>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', color: '#fff',fontSize: '14px' }}>{t("settings.google_admob.admob_user_targeting","AdMob User Targeting")}</Typography>
                                            <Select
                                                {...field}
                                                value={field.value || ""}
                                                displayEmpty
                                                sx={{
                                                    color: "#fff",
                                                    fontWeight: 400,
                                                    "& .MuiSelect-select": {
                                                        padding: "12px 20px",
                                                    },
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        border: "1px solid rgba(230, 210, 249, 0.1)",
                                                    },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        border: "1px solid rgba(230, 210, 249, 0.1)",
                                                    },
                                                    "& .MuiSvgIcon-root": {
                                                        color: "#fff",
                                                    },
                                                }}
                                            >
                                                {targetOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                    {t(`admob_options.${option.value}`, option.label)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>                                        
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading || !hasAction(MODULE_KEY, 'update')}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '999px',
                                        fontWeight: '400',
                                        fontSize: '15px',
                                        lineHeight: '24px',
                                        textTransform: 'capitalize',
                                        background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                        minWidth: '200px',
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : t('update_settings', 'Update Settings')}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                }}
            />
        </Box>
    );
};

export default GoogleAdMobSettings;
